import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { extractApiError, getErrorMessage } from '@/shared/utils/handle-api-error'
import { clientAccountsAdminV2Service } from '../services/client-accounts-admin-v2.service'

/**
 * Las escrituras de la consola de cuentas.
 *
 * TODAS invalidan la familia entera (`queryKeys.clientAccounts.all`) y no la clave
 * puntual: bloquear una cuenta cambia su detalle, su fila en el listado, los contadores
 * de las tarjetas y la pestaña «App» de la ficha del cliente. Enumerarlas en cada
 * mutación es la forma segura de olvidarse de una y dejar la pantalla mintiendo.
 *
 * ── EL ERROR SE RAMIFICA POR `code`, NUNCA POR EL MENSAJE ────────────────────
 *
 * El mensaje es traducción; el código es contrato. Y acá hay dos que la pantalla tiene
 * que distinguir de verdad, porque cambian lo que la persona hace después:
 * `CLIENT_ACCOUNT_INVITATION_THROTTLED` («ya se envió, esperá») y
 * `CLIENT_ACCOUNT_EMAIL_TAKEN` («ese correo es de otra cuenta»).
 */

const CLIENT_ACCOUNT_ERROR_CODES = {
  INVITATION_THROTTLED: 'CLIENT_ACCOUNT_INVITATION_THROTTLED',
  EMAIL_TAKEN: 'CLIENT_ACCOUNT_EMAIL_TAKEN',
  ACCOUNT_BLOCKED: 'CLIENT_APP_ACCOUNT_BLOCKED'
} as const

/** «faltan 8 minutos» en vez de «faltan 467 segundos», que nadie lee. */
function humanizeWait(seconds: number): string {
  if (seconds <= 60) return 'menos de un minuto'
  const minutes = Math.ceil(seconds / 60)
  return `${minutes} minuto${minutes === 1 ? '' : 's'}`
}

function useMutationContext() {
  const { auth } = useAuth()
  const queryClient = useQueryClient()
  const accessToken = auth?.v2AccessToken ?? ''
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.clientAccounts.all })
    // ☠️ Y TAMBIÉN el estado de acceso móvil de las negociaciones.
    //
    // Ese estado NO se guarda: se CALCULA a partir de la cuenta y sus grants, así que
    // cualquier mutación de acá lo cambia. Vive en otra familia de claves porque se
    // consulta desde el expediente, y sin esta línea revocar un acceso desde la tarjeta de
    // la negociación dejaba la propia tarjeta diciendo que el cliente todavía lo tiene —
    // la pantalla mintiendo justo sobre la acción que se acaba de hacer en ella.
    void queryClient.invalidateQueries({ queryKey: queryKeys.negotiations.mobileAccessAll })
  }
  return { accessToken, invalidate }
}

export function useClientAccountMutations() {
  const { accessToken, invalidate } = useMutationContext()

  const create = useMutation({
    mutationFn: async (input: { clientId: string; email: string }) =>
      await clientAccountsAdminV2Service.create(accessToken, input),
    onSuccess: () => {
      invalidate()
      // Se nombra lo que NO pasó: crear la cuenta no avisa a nadie. Sin decirlo, «cuenta
      // creada» se lee como «ya le escribimos», y el cliente queda esperando un correo
      // que nunca se mandó.
      toast.success('Cuenta creada. Todavía no se le envió nada al cliente.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })

  const resendInvitation = useMutation({
    mutationFn: async (id: string) =>
      await clientAccountsAdminV2Service.resendInvitation(accessToken, id),
    onSuccess: () => {
      invalidate()
      toast.success('Invitación enviada.')
    },
    onError: (error) => {
      const apiError = extractApiError(error)
      if (apiError?.errorCode === CLIENT_ACCOUNT_ERROR_CODES.INVITATION_THROTTLED) {
        const retryAfter = (apiError.details as { retryAfter?: number } | undefined)?.retryAfter
        toast.warning(
          retryAfter
            ? `Ya se envió hace poco. Puedes reenviarla en ${humanizeWait(retryAfter)}.`
            : 'Ya se envió hace poco. Espera unos minutos para reenviarla.'
        )
        return
      }
      if (apiError?.errorCode === CLIENT_ACCOUNT_ERROR_CODES.ACCOUNT_BLOCKED) {
        toast.error('La cuenta está bloqueada. Reactívala antes de invitar.')
        return
      }
      toast.error(getErrorMessage(error))
    }
  })

  const updateEmail = useMutation({
    mutationFn: async (input: { id: string; email: string }) =>
      await clientAccountsAdminV2Service.updateEmail(accessToken, input.id, input.email),
    onSuccess: () => {
      invalidate()
      // El cierre de sesiones se nombra porque es un efecto que la persona no pidió y sí
      // va a notar: el cliente queda afuera de la app hasta que vuelva a entrar.
      toast.success('Correo actualizado. Se cerraron las sesiones abiertas del cliente.')
    },
    onError: (error) => {
      const apiError = extractApiError(error)
      if (apiError?.errorCode === CLIENT_ACCOUNT_ERROR_CODES.EMAIL_TAKEN) {
        toast.error('Ese correo ya pertenece a otra cuenta de la app.')
        return
      }
      toast.error(getErrorMessage(error))
    }
  })

  const grant = useMutation({
    mutationFn: async (input: { id: string; propertyId: string; expiresAt?: string }) =>
      await clientAccountsAdminV2Service.grant(accessToken, input.id, {
        propertyId: input.propertyId,
        ...(input.expiresAt ? { expiresAt: input.expiresAt } : {})
      }),
    onSuccess: () => {
      invalidate()
      toast.success('Inmueble concedido. El cliente lo verá en su próxima consulta.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })

  const revokeGrant = useMutation({
    mutationFn: async (input: { id: string; grantId: string }) =>
      await clientAccountsAdminV2Service.revokeGrant(accessToken, input.id, input.grantId),
    onSuccess: () => {
      invalidate()
      toast.success('Acceso al inmueble revocado.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })

  const revokeSession = useMutation({
    mutationFn: async (input: { id: string; sessionId: string }) =>
      await clientAccountsAdminV2Service.revokeSession(accessToken, input.id, input.sessionId),
    onSuccess: () => {
      invalidate()
      toast.success('Sesión cerrada.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })

  const revokeAllSessions = useMutation({
    mutationFn: async (id: string) =>
      await clientAccountsAdminV2Service.revokeAllSessions(accessToken, id),
    onSuccess: () => {
      invalidate()
      toast.success('Se cerraron todas las sesiones del cliente.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })

  const block = useMutation({
    mutationFn: async (id: string) => await clientAccountsAdminV2Service.block(accessToken, id),
    onSuccess: () => {
      invalidate()
      toast.success('Cuenta bloqueada. Se cerraron todas sus sesiones.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })

  const reactivate = useMutation({
    mutationFn: async (id: string) =>
      await clientAccountsAdminV2Service.reactivate(accessToken, id),
    onSuccess: () => {
      invalidate()
      // El cliente NO vuelve solo: tiene que pedir un código nuevo. Decirlo evita el
      // «ya la reactivé y sigue sin entrar» de diez minutos después.
      toast.success('Cuenta reactivada. El cliente deberá ingresar con un código nuevo.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })

  return {
    create,
    resendInvitation,
    updateEmail,
    grant,
    revokeGrant,
    revokeSession,
    revokeAllSessions,
    block,
    reactivate
  }
}
